/**
 * Accessible text extraction utilities for KaTeX-rendered MathML.
 */
import katex from 'katex';

const MATHML_TRANSLATION_KEYS = {
  plus: 'mathml.plus',
  minus: 'mathml.minus',
  times: 'mathml.times',
  dividedBy: 'mathml.dividedBy',
  equals: 'mathml.equals',
  lessThan: 'mathml.lessThan',
  greaterThan: 'mathml.greaterThan',
  lessThanOrEqual: 'mathml.lessThanOrEqual',
  greaterThanOrEqual: 'mathml.greaterThanOrEqual',
  plusOrMinus: 'mathml.plusOrMinus',
  approximately: 'mathml.approximately',
  notEqual: 'mathml.notEqual',
  infinity: 'mathml.infinity',
  degrees: 'mathml.degrees',
  sum: 'mathml.sum',
  product: 'mathml.product',
  integral: 'mathml.integral',
  squareRoot: 'mathml.squareRoot',
  openParenthesis: 'mathml.openParenthesis',
  closeParenthesis: 'mathml.closeParenthesis',
  openBracket: 'mathml.openBracket',
  closeBracket: 'mathml.closeBracket',
  openBrace: 'mathml.openBrace',
  closeBrace: 'mathml.closeBrace',
  over: 'mathml.over',
  toThePowerOf: 'mathml.toThePowerOf',
  subscript: 'mathml.subscript',
  rootOf: 'mathml.rootOf',
  squareRootOf: 'mathml.squareRootOf',
  cubeRootOf: 'mathml.cubeRootOf',
  nthRootOf: 'mathml.nthRootOf',
  nthRootOfWithOrdinal: 'mathml.nthRootOfWithOrdinal',
  matrix: 'mathml.matrix',
  matrixWithRowsAndColumns: 'mathml.matrixWithRowsAndColumns',
  rowLabel: 'mathml.rowLabel',
  integralFrom: 'mathml.integralFrom',
  integralTo: 'mathml.integralTo',
} as const;

export type TranslateFunction = (key: string) => string;
export type RenderMathFunction = (latex: string) => string;

const defaultTranslate: TranslateFunction = (key) => key;

function getOrdinalSuffix(numericString: string, t: TranslateFunction): string {
  const n = parseInt(numericString, 10);
  const tens = Number.isFinite(n) ? n % 100 : NaN;
  let cat: 'st' | 'nd' | 'rd' | 'th' = 'th';
  if (Number.isFinite(n) && (tens < 11 || tens > 13)) {
    const last = n % 10;
    if (last === 1) {
      cat = 'st';
    } else if (last === 2) {
      cat = 'nd';
    } else if (last === 3) {
      cat = 'rd';
    } else {
      cat = 'th';
    }
  }
  const translated = t(`mathml.ordinalSuffix.${cat}`);
  return translated && translated !== `mathml.ordinalSuffix.${cat}` ? translated : cat;
}

/*
 * Chemistry extraction utils
 */
/**
 * We use this as a special string to replace chemical equations into the processed LaTeX.
 * The string of numbers is chosen as they will remain together even after rendered as MathML which allows
 * us to easily substitute it afterwards.
 */
function indexToSubstitution(index: number): string {
  return `123123${index}321321`;
}

function extractChemistryFragments(expression: string): {
  expression: string;
  fragments: string[];
} {
  let expressionResult: string = '';
  const fragments: string[] = [];

  let nextChemistryIndex = expression.indexOf('\\ce');
  while (nextChemistryIndex !== -1) {
    expressionResult += expression.substring(0, nextChemistryIndex);
    expression = expression.substring(nextChemistryIndex + 3);

    let chemistryExpression = '';
    let bracketLevel = 0;
    for (let i = 0; i < expression.length; ++i) {
      if (expression[i] === '{') {
        bracketLevel += 1;
        if (bracketLevel > 1) chemistryExpression += '{';
      } else if (expression[i] === '}') {
        bracketLevel -= 1;
        if (bracketLevel > 1) chemistryExpression += '}';
      } else {
        chemistryExpression += expression[i];
      }

      // Consume a character
      expression = expression.substring(1);
      i--;

      if (bracketLevel === 0) {
        expressionResult += `{{ ${indexToSubstitution(fragments.length)} }}`;
        fragments.push(chemistryExpression);
        break;
      }
    }

    nextChemistryIndex = expression.indexOf('\\ce');
  }

  expressionResult += expression;

  return {
    expression: expressionResult,
    fragments,
  };
}

function substituteChemistryFragments(expression: string, fragments: string[]) {
  for (let i = 0; i < fragments.length; i++) {
    expression = expression.replace(indexToSubstitution(i), fragments[i]);
  }
  return expression;
}

/*
 * Accessible text utilities
 */
export function generateAccessibleMathLabel(
  latex: string,
  options: {renderMathMl?: RenderMathFunction; translate?: TranslateFunction} = {}
) {
  const resolvedRenderMathMl: RenderMathFunction = options.renderMathMl || katex.renderToString;
  const resolvedTranslate: TranslateFunction = options.translate || ((k: string) => k);

  const {expression, fragments} = extractChemistryFragments(latex);

  const renderedExpression = resolvedRenderMathMl(expression);
  const accessibleExpression = calculateMathmlText(renderedExpression, resolvedTranslate);
  const accessibleChemicalFragments = fragments.map((f) =>
    extractChemistryText(f, resolvedTranslate)
  );

  return substituteChemistryFragments(accessibleExpression, accessibleChemicalFragments);
}

function calculateMathmlText(katexHtml: string, t: TranslateFunction = defaultTranslate): string {
  try {
    const doc = new DOMParser().parseFromString(katexHtml, 'text/html');
    const math = doc.querySelector('.katex-mathml math');
    if (!math) return '';

    const text = extractMathMLText(math, t).join(' ');
    return text
      .replace(/\s+/g, ' ')
      .replace(/\b(\w+)\s+\1\b/g, '$1')
      .trim();
  } catch (e) {
    console.warn('Failed to extract MathML text:', e);
    return '';
  }
}

function extractChemistryText(expr: string, t: TranslateFunction): string {
  let text = expr.replace(/^\\ce\{(.*)\}$/, '$1');

  try {
    const map: Record<string, keyof typeof MATHML_TRANSLATION_KEYS> = {
      '[': 'openBracket',
      ']': 'closeBracket',
      '(': 'openParenthesis',
      ')': 'closeParenthesis',
      '{': 'openBrace',
      '}': 'closeBrace',
      '-': 'minus',
    };

    /*
     * Replace states
     */
    const states = {
      l: 'liquid',
      s: 'solid',
      aq: 'aqueous',
      g: 'gas',
    };

    Object.entries(states).forEach(([k, v]) => {
      text = text.replaceAll(`(${k})`, ` ${v}`);
    });

    /*
     * Arrows
     */
    const arrows = {
      '->': 'right arrow',
      '<-': 'left arrow',
      '<=>': 'equilibrium',
    };
    Object.entries(arrows).forEach(([k, v]) => {
      text = text.replaceAll(k, v);
    });

    return text
      .split('')
      .map((ch) => {
        const key = map[ch];
        return key ? ` ${t(MATHML_TRANSLATION_KEYS[key])} ` : ch;
      })
      .join('')
      .replace(/\s+/g, ' ')
      .trim();
  } catch (e) {
    console.warn('Failed to extract chemistry text', e);
    return text; // Return the relatively untransformed text
  }
}

function extractMathMLText(el: Element, t: TranslateFunction): string[] {
  const textParts: string[] = [];
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const txt = node.textContent?.trim();
      if (txt) textParts.push(txt);
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const tag = (node as Element).tagName.toLowerCase();
    const kids = Array.from((node as Element).children);
    const extract = (i: number): string[] => extractMathMLText(kids[i], t);

    switch (tag) {
      case 'mi':
      case 'mn':
      case 'mo':
      case 'mtext':
      case 'mspace':
      case 'ms': {
        const text = node.textContent?.trim();
        if (text) textParts.push(cleanMathMLText(text, t));
        break;
      }

      case 'mfrac':
        if (kids.length >= 2)
          textParts.push(
            `${extract(0).join(' ')} ${t(MATHML_TRANSLATION_KEYS.over)} ${extract(1).join(' ')}`
          );
        break;

      case 'msup':
        if (kids.length >= 2)
          textParts.push(
            `${extract(0).join(' ')} ${t(MATHML_TRANSLATION_KEYS.toThePowerOf)} ${extract(1).join(' ')}`
          );
        break;

      case 'msub':
        if (kids.length >= 2)
          textParts.push(
            `${extract(0).join(' ')} ${t(MATHML_TRANSLATION_KEYS.subscript)} ${extract(1).join(' ')}`
          );
        break;

      case 'msubsup':
        if (kids.length >= 3) {
          const base = extract(0);
          const sub = extract(1);
          const sup = extract(2);
          let text = base.join(' ').trim();

          const isIntegral = text === '∫';
          if (isIntegral) {
            text = t(MATHML_TRANSLATION_KEYS.integral);
          }

          if (sub.length) {
            if (isIntegral) {
              text += ` ${t(MATHML_TRANSLATION_KEYS.integralFrom)} ${sub.join(' ')}`;
            } else {
              text += ` ${t(MATHML_TRANSLATION_KEYS.subscript)} ${sub.join(' ')}`;
            }
          }
          if (sup.length) {
            if (isIntegral) {
              text += ` ${t(MATHML_TRANSLATION_KEYS.integralTo)} ${sup.join(' ')}`;
            } else {
              text += ` ${t(MATHML_TRANSLATION_KEYS.toThePowerOf)} ${sup.join(' ')}`;
            }
          }
          textParts.push(text);
        }
        break;

      case 'mroot':
        if (kids.length >= 2) {
          const radicand = extract(0);
          const index = extract(1);
          const rad = radicand.join(' ');
          if (!index.length) {
            textParts.push(`${t(MATHML_TRANSLATION_KEYS.squareRootOf)} ${rad}`);
            break;
          }
          const n = index.join(' ');
          let phrase: string;
          if (n === '2') {
            phrase = t(MATHML_TRANSLATION_KEYS.squareRootOf);
          } else if (n === '3') {
            phrase = t(MATHML_TRANSLATION_KEYS.cubeRootOf);
          } else {
            phrase = t(MATHML_TRANSLATION_KEYS.nthRootOfWithOrdinal)
              .replace('{n}', n)
              .replace('{ordinal}', getOrdinalSuffix(n, t));
          }
          textParts.push(`${phrase} ${rad}`);
        }
        break;

      case 'msqrt': {
        const radicand = extractMathMLText(node as Element, t);
        if (radicand.length) {
          textParts.push(`${t(MATHML_TRANSLATION_KEYS.squareRootOf)} ${radicand.join(' ')}`);
        }
        break;
      }

      case 'mtable': {
        const rows = kids.filter((k) => k.tagName.toLowerCase() === 'mtr');
        const rowCount = rows.length;
        const firstRow = rows[0];
        const colCount = firstRow
          ? Array.from(firstRow.children).filter((c) => c.tagName.toLowerCase() === 'mtd').length
          : 0;

        if (rowCount > 0 && colCount > 0) {
          const template = t(MATHML_TRANSLATION_KEYS.matrixWithRowsAndColumns);
          const phrase = template
            .replace('{rows}', String(rowCount))
            .replace('{cols}', String(colCount));
          textParts.push(`${phrase},`);
        } else {
          textParts.push(t(MATHML_TRANSLATION_KEYS.matrix));
        }

        for (const [i, row] of rows.entries()) {
          const cells = Array.from(row.children).filter((c) => c.tagName.toLowerCase() === 'mtd');
          const cellTexts = cells
            .map((c) => extractMathMLText(c as Element, t).join(' '))
            .filter((s) => s.length > 0);
          if (cellTexts.length) {
            const suffix = i < rows.length - 1 ? ';' : '.';
            const rowLabelTemplate = t(MATHML_TRANSLATION_KEYS.rowLabel);
            const rowLabel = rowLabelTemplate.replace('{n}', String(i + 1));
            textParts.push(`${rowLabel} ${cellTexts.join(', ')}${suffix}`);
          }
        }
        break;
      }

      case 'annotation':
      case 'annotation-xml':
        // Skip LaTeX annotation elements - they contain source code, not readable text
        break;

      default: {
        const childText = extractMathMLText(node as Element, t);
        if (childText.length) textParts.push(...childText);
      }
    }
  }
  return textParts;
}

function cleanMathMLText(text: string, t: TranslateFunction): string {
  const map: Record<string, keyof typeof MATHML_TRANSLATION_KEYS> = {
    '+': 'plus',
    '-': 'minus',
    '−': 'minus',
    '×': 'times',
    '*': 'times',
    '÷': 'dividedBy',
    '=': 'equals',
    '<': 'lessThan',
    '>': 'greaterThan',
    '≤': 'lessThanOrEqual',
    '≥': 'greaterThanOrEqual',
    '±': 'plusOrMinus',
    '≈': 'approximately',
    '≠': 'notEqual',
    '∞': 'infinity',
    '°': 'degrees',
    '∑': 'sum',
    '∏': 'product',
    '∫': 'integral',
    '√': 'squareRoot',
    '(': 'openParenthesis',
    ')': 'closeParenthesis',
    '[': 'openBracket',
    ']': 'closeBracket',
    '{': 'openBrace',
    '}': 'closeBrace',
  };

  if (map[text]) return t(MATHML_TRANSLATION_KEYS[map[text]]);

  const functions: Record<string, string> = {
    sin: 'sine',
    cos: 'cosine',
    tan: 'tangent',
    log: 'logarithm',
    ln: 'natural logarithm',
    exp: 'exponential',
  };

  return functions[text.toLowerCase()] || text;
}
